import { useEffect, useState } from 'react';
// Client error reporter (2026-09-22, Jaime): visible error toasts are
// registered server-side (fire-and-forget, throttled/deduped upstream).
import { reportClientError } from '../lib/clientErrorReporter';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  // BUG #141 (2026-09-24): expected client-side validation messages (e.g.
  // invalid file type picked by the user) are not defects — they must NOT be
  // filed to /api/client-errors. Only real failure toasts are reported.
  report?: boolean;
}

interface ToastProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const icons: Record<ToastType, string> = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️'
};

const styles: Record<ToastType, string> = {
  success: 'bg-green-500/20 border-green-500/30 text-green-400',
  error: 'bg-red-500/20 border-red-500/30 text-red-400',
  warning: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
  info: 'bg-blue-500/20 border-blue-500/30 text-blue-400'
};

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [progress, setProgress] = useState(100);
  const duration = toast.duration || 5000;

  useEffect(() => {
    // CLIENT_ERROR: a visible error toast means something failed for the user.
    // Expected validations opt out via report === false (BUG #141).
    if (toast.type === 'error' && toast.report !== false) {
      reportClientError({
        message: toast.message || 'error toast',
        level: 'error',
        context: { kind: 'toast', route: window.location.pathname },
      });
    }
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
        onRemove(toast.id);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [toast.id, duration, onRemove]);

  return (
    <div className={`relative overflow-hidden rounded-lg border p-4 shadow-lg backdrop-blur-sm ${styles[toast.type]} animate-slide-in`}>
      <div className="flex items-start gap-3">
        <span className="text-xl">{icons[toast.type]}</span>
        <p className="text-sm font-medium flex-1">{toast.message}</p>
        <button
          onClick={() => onRemove(toast.id)}
          className="text-current opacity-60 hover:opacity-100 transition"
        >
          ✕
        </button>
      </div>
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-current opacity-30 transition-all duration-100"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

export default function Toast({ toasts, onRemove }: ToastProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}

// Hook for using toast
export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (
    message: string,
    type: ToastType = 'info',
    duration?: number,
    opts?: { report?: boolean },
  ) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration, report: opts?.report }]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const success = (message: string, duration?: number) => addToast(message, 'success', duration);
  const error = (message: string, duration?: number, opts?: { report?: boolean }) =>
    addToast(message, 'error', duration, opts);
  const warning = (message: string, duration?: number) => addToast(message, 'warning', duration);
  const info = (message: string, duration?: number) => addToast(message, 'info', duration);

  return { toasts, addToast, removeToast, success, error, warning, info };
}
